// src/components/PrefetchLink.jsx
// Composant Link optimisé avec prefetching pour améliorer la navigation
import { NavLink } from "react-router-dom";
import { useEffect, useRef } from "react";

/**
 * Composant Link avec prefetching automatique au survol
 * Précharge les routes au survol pour une navigation instantanée
 */
export default function PrefetchLink({ to, children, prefetch = true, ...props }) {
  const linkRef = useRef(null);
  const prefetchedRef = useRef(false);

  useEffect(() => {
    if (!prefetch || prefetchedRef.current) return;

    const link = linkRef.current;
    if (!link) return;

    const handlePrefetch = () => {
      if (prefetchedRef.current) return;
      
      // Précharger via link prefetch (méthode standard du navigateur)
      // Cela précharge le JavaScript de la route lazy-loaded
      const prefetchLink = document.createElement("link");
      prefetchLink.rel = "prefetch";
      prefetchLink.href = to;
      document.head.appendChild(prefetchLink);
      
      prefetchedRef.current = true;
    };

    // Précharger au survol avec un léger délai pour éviter les préchargements inutiles
    let timeoutId;
    const handleMouseEnterDelayed = () => {
      timeoutId = setTimeout(handlePrefetch, 100);
    };

    link.addEventListener("mouseenter", handleMouseEnterDelayed);
    link.addEventListener("touchstart", handlePrefetch, {
      once: true,
      passive: true,
    });

    return () => {
      link.removeEventListener("mouseenter", handleMouseEnterDelayed);
      link.removeEventListener("touchstart", handlePrefetch);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [to, prefetch]);

  return (
    <NavLink ref={linkRef} to={to} {...props}>
      {children}
    </NavLink>
  );
}

